
/**
 * Module dependencies.
 */

import { ComponentType, ReactNode } from 'react';
import { Fill } from 'react-components/layout';
import { Icon } from 'react-components/media';
import { Loading } from '../loading';
import { RouterLink } from '../links/router-link';
import { color } from 'react-components/styles';
import { get } from 'lodash';
import { ifProp, switchProp, theme } from 'styled-tools';
import styled, { css } from 'styled-components';

/**
 * `Size` type.
 */

type Size = 'extraLarge' | 'large' | 'medium' | 'small';

/**
 * Export `MaskedButtonProps` type.
 */

export type MaskedButtonProps = {
  'aria-label'?: string;
  as?: string | ComponentType<any>;
  children?: ReactNode;
  colorTheme?: keyof typeof colorThemes;
  disabled?: boolean;
  hasBigMask?: boolean;
  href?: string;
  icon: string;
  iconSize?: string;
  isSubmitting?: boolean;
  onClick?: () => void;
  size?: Size | string;
  to?: any;
  type?: 'button' | 'submit';
};

/**
 * Color themes.
 */

const colorThemes = {
  primary: {
    backgroundColor: color('blue500'),
    color: color('white'),
    disabled: {
      backgroundColor: color('blue100'),
      color: color('blue200')
    },
    interaction: {
      backgroundColor: color('blue600')
    }
  },
  secondary: {
    backgroundColor: color('gray300'),
    color: color('gray500'),
    disabled: {
      backgroundColor: color('gray200'),
      color: color('gray400')
    },
    interaction: {
      backgroundColor: color('gray500'),
      color: color('white')
    }
  },
  white: {
    backgroundColor: color('white'),
    color: color('gray500'),
    disabled: {
      color: color('gray300')
    }
  }
};

/**
 * `SmallMaskIcon` styled component.
 */

const SmallMaskIcon = styled(Fill).attrs({ as: 'span' })`
  align-items: center;
  clip-path: url(#slyk-mask);
  display: flex;
  justify-content: center;
  overflow: hidden;
  transition: ${theme('animations.defaultTransition')};
  transition-property: background-color, color, transform;
`;

/**
 * Button themes.
 */

const buttonThemes = Object.entries(colorThemes).reduce((result, [themeName, themeSchema]) => ({
  ...result,
  [themeName]: css`
    ${SmallMaskIcon} {
      background-color: ${get(themeSchema, 'backgroundColor')};
      color: ${get(themeSchema, 'color')};
    }

    ${ifProp('disabled', css`
      ${SmallMaskIcon} {
        background-color: ${get(themeSchema, 'disabled.backgroundColor')};
        color: ${get(themeSchema, 'disabled.color')};
      }
    `)}

    &:focus,
    &:hover {
      ${SmallMaskIcon} {
        background-color: ${get(themeSchema, 'interaction.backgroundColor')};
        color: ${get(themeSchema, 'interaction.color')};
      }
    }
  `
}), {});

/**
 * `BigMaskIcon` styled component.
 */

const BigMaskIcon = styled(Fill).attrs({ as: 'span' })<{ isVisible: boolean }>`
  background-color: ${color.transparentize('gray200', 0.6)};
  clip-path: url(#slyk-mask);
  opacity: 0;
  transition: ${theme('animations.defaultTransition')};
  transition-property: opacity, transform;

  ${ifProp('isVisible', css`
    opacity: 1;
  `)}
`;

/**
 * `Button` styled component.
 */

const Button = styled.button.attrs<Pick<MaskedButtonProps, 'as' | 'href' | 'to' | 'type'>>(({ as, href, to, type }) => {
  const element = as || to && RouterLink || href && 'a' || 'button';

  return {
    as: element,
    type: type || (element === 'button' ? 'button' : undefined)
  };
})`
  -webkit-tap-highlight-color: transparent;
  align-items: center;
  appearance: none;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  padding: 0;
  position: relative;
  row-gap: 8px;
  text-align: center;

  ${ifProp('hasBigMask', css`
    &:focus,
    &:hover {
      ${BigMaskIcon} {
        transform: scale(1.1);
      }

      ${SmallMaskIcon} {
        transform: scale(0.9);
      }
    }
  `)}


  ${ifProp('disabled', css`
    ${SmallMaskIcon} {
      cursor: default;
      pointer-events: none;
    }
  `)}

  ${switchProp('colorTheme', buttonThemes)}

  &:hover,
  &:focus {
    outline: none;
    text-decoration: none;
  }
`;

/**
 * `MaskIconWrapper` styled component.
 */

const MaskIconWrapper = styled.span<{ isVisible: boolean; isWhite: boolean }>`
  align-items: center;
  display: flex;
  justify-content: center;
  position: relative;

  ${ifProp('isWhite', css`
    filter: 
      drop-shadow(0 1px 2px rgba(89, 103, 124, 0.08)) 
      drop-shadow(0 1px 5px rgba(89, 103, 124, 0.1)) 
      drop-shadow(0 12px 15px rgba(89, 103, 124, 0.05));
  `)}

  ${ifProp('isVisible', css`
    height: 96px;
    width: 96px;
  `)}
`;

/**
 * `IconWrapper` styled component.
 */

const IconWrapper = styled.span<{ size: Size | string }>`
  position: relative;

  ${switchProp('size', {
    extraLarge: css`
      height: 68px;
      width: 68px;
    `,
    large: css`
      height: 64px;
      width: 64px;
    `,
    medium: css`
      height: 48px;
      width: 48px;
    `,
    small: css`
      height: 32px;
      width: 32px;
    `
  }, css`
    height: ${props => (props as unknown as { size: string }).size};
    width: ${props => (props as unknown as { size: string }).size};
  `)}
`;

/**
 * `StyledIcon` styled component.
 */

const StyledIcon = styled(Icon)<{ size: Size | string }>`
  ${switchProp('size', {
    extraLarge: css`
      max-width: 40px;
      width: 40px;
    `,
    large: css`
      max-width: 32px;
      width: 32px;
    `,
    medium: css`
      max-width: 24px;
      width: 24px;
    `,
    small: css`
      max-width: 16px;
      width: 16px;
    `
  }, css`
    height: ${props => (props as unknown as { size: string }).size};
    width: ${props => (props as unknown as { size: string }).size};
  `)}
`;

/**
 * Export `MaskedButton` component.
 */

export function MaskedButton(props: MaskedButtonProps): JSX.Element {
  const {
    children,
    colorTheme = 'primary',
    hasBigMask = false,
    icon,
    iconSize,
    isSubmitting,
    size = 'medium',
    ...rest
  } = props;

  return (
    <Button
      {...rest}
      colorTheme={colorTheme}
      hasBigMask={hasBigMask}
    >
      <MaskIconWrapper
        isVisible={hasBigMask}
        isWhite={colorTheme === 'white'}
      >
        <BigMaskIcon isVisible={hasBigMask} />

        <IconWrapper size={size}>
          <SmallMaskIcon>
            {isSubmitting ? (
              <Loading
                active
                relative
              />
            ) : (
              <StyledIcon
                aria-hidden
                color={'inherit'}
                icon={icon}
                size={iconSize ?? size}
              />
            )}
          </SmallMaskIcon>
        </IconWrapper>
      </MaskIconWrapper>

      {children}
    </Button>
  );
}
